import axios from 'axios';
import { getAuthorazieHeader } from './service';
import { url, aiPilotUrl } from './config';

const BASE_URL = `${url}/unifiedAlerts`;
const BASE_URL_AZURE = `${url}/unifiedAlertsAzure`;

export interface AlertSummaryResponse {
  total: Record<string, number>;
}

export interface Alert {
  alertId: string;
  description: string;
  source: string;
  severity: string;
  status: string;
  assignmentGroup: string;
  sector: string;
  program: string;
  raw?: any;
}

export interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AlertsResponse {
  data: Alert[];
  pagination: PaginationInfo;
}

export interface TrendData {
  [key: string]: any;
  count: number;
  severityBreakdown: Record<string, number>;
}

type TrendResponse = {
  totalUnique: number;
  data: any[];

  avgUniquePerDay: any,
  peakDay: any,
  peakDayUnique: any,
  peakSector: any,
  peakSectorCount: any,
  trendPercent: any,
  trend: any,
};

export interface SourceData {
  source: string;
  count: number;
  percentage: number;
}

export interface FilterOptions {
  severity: string[];
  status: string[];
  sector: string[];
  program: string[];
  source: string[];
  assignmentGroup: string[];
  domains: string[];
  businessUnits: string[];
}

const normalizeTopLevelDandAIAG = (params: Record<string, any>): Record<string, any> => {
  let requestParams = { ...(params ?? {}) };
  if (requestParams.isDandAIAG !== undefined && requestParams.isDandAIAG !== null) {
    const rawVal = requestParams.isDandAIAG;
    if (typeof rawVal === 'string') {
      requestParams.isDandAIAG = rawVal.toLowerCase() === 'yes' || rawVal.toLowerCase() === 'true';
    } else if (Array.isArray(rawVal)) {
      if (rawVal.length > 0) {
        const first = rawVal[0]?.value ?? rawVal[0];
        requestParams.isDandAIAG = String(first).toLowerCase() === 'yes' || String(first).toLowerCase() === 'true';
      } else {
        delete requestParams.isDandAIAG;
      }
    } else if (typeof rawVal === 'boolean') {
      requestParams.isDandAIAG = rawVal;
    }
  }
  return requestParams;
};

export const getSummary = async (params?: Record<string, any>): Promise<AlertSummaryResponse> => {
  const normalizedParams = normalizeTopLevelDandAIAG(params ?? {});
  const response = await axios.post(`${BASE_URL}/alerts-summary`, normalizedParams, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getAlerts = async (params: {
  page?: number;
  pageSize?: number;
  search?: string;
  severity?: string;
  status?: string;
  sector?: string;
  program?: string;
  source?: string;
  assignmentGroup?: string;
  domains?: string;
  businessUnits?: string;
  timeRange?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  exportType?: string;
  isDandAIAG?: boolean;
  [key: string]: any;
}): Promise<AlertsResponse> => {
  const normalizedParams = normalizeTopLevelDandAIAG(params ?? {});
  const response = await axios.post(`${BASE_URL}/alerts`, normalizedParams, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getAlertsGroupedChildren = async (params: any): Promise<any> => {
  const normalizedParams = normalizeTopLevelDandAIAG(params ?? {});
  const response = await axios.post(`${BASE_URL}/getChildren`, normalizedParams, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const exportAlerts = async (params: Record<string, any>): Promise<void> => {
  const response = await axios.post(`${BASE_URL}/alerts`, { ...params, exportType: 'excel' }, {
    headers: getAuthorazieHeader(),
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'] ?? '';
  const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? `UnifiedAlerts-${new Date().toISOString()}.xlsx`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getTrendBySector = async (params: any): Promise<TrendData[]> => {
  const response = await axios.post(`${BASE_URL}/trend/sector`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getTrendByProgram = async (params: any): Promise<TrendData[]> => {
  const response = await axios.post(`${BASE_URL}/trend/program`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getSources = async (params: any): Promise<SourceData[]> => {
  const response = await axios.post(`${BASE_URL}/sources`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getPieData = async (params: any): Promise<SourceData[]> => {
  const response = await axios.post(`${BASE_URL}/open-alerts`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getMostImpactedApps = async (params: any): Promise<any[]> => {
  const response = await axios.post(`${BASE_URL}/most-impacted-apps`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getDropDownData = async (): Promise<any> => {
  const response = await axios.get(`${BASE_URL}/alerts-dropdown`, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const createComments = async (params: { alertId: string; comments: string }): Promise<any> => {
  const response = await axios.post(`${BASE_URL}/alerts-comments`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};


export const getSummaryAzure = async (params?: { startDate?: string; endDate?: string }): Promise<AlertSummaryResponse> => {
  const response = await axios.post(`${BASE_URL_AZURE}/alerts-summary`, params ?? {}, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getAlertsAzure = async (params: {
  page?: number;
  pageSize?: number;
  search?: string;
  severity?: string;
  status?: string;
}): Promise<AlertsResponse> => {
  const response = await axios.post(`${BASE_URL_AZURE}/alerts`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const exportAlertsAzure = async (params: Record<string, any>): Promise<void> => {
  const response = await axios.post(`${BASE_URL_AZURE}/alerts`, { ...params, exportType: 'excel' }, {
    headers: getAuthorazieHeader(),
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'] ?? '';
  const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? `UnifiedAlerts-${new Date().toISOString()}.xlsx`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getDropDownDataAzure = async (): Promise<any> => {
  const response = await axios.get(`${BASE_URL_AZURE}/alerts-dropdown`, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};


export const getFiltersData = async (): Promise<any> => {
  const response = await axios.get(`${BASE_URL}/load-filters`, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const saveFiltersData = async (params: {
  filters?: any;
}): Promise<any> => {
  const response = await axios.post(`${BASE_URL}/store-filters`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getAzureFiltersData = async (): Promise<any> => {
  const response = await axios.get(`${BASE_URL_AZURE}/load-filters`, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
}

export const saveAzureFiltersData = async (params: {
  filters?: any;
}): Promise<any> => {
  const response = await axios.post(`${BASE_URL_AZURE}/store-filters`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

const itsm_summary_url: any = {
  'Incidents': `${url}/unifiedOps/incident-summary`,
  'ChangeRequests': `${url}/unifiedOps/change-summary`,
  'ServiceRequests': `${url}/unifiedOps/service-summary`,
  'ProblemTickets': `${url}/unifiedOps/problem-summary`,
}

const itsmEndpoints: Record<string, string> = {
  incidents: `${url}/unifiedOps/incidents`,
  change: `${url}/unifiedOps/change-request`,
  service: `${url}/unifiedOps/service-request`,
  problemTickets: `${url}/unifiedOps/problem-tickets`,
};

const itsmGroups: Record<string, string[]> = {
  incidents: ['Incidents', 'P3', 'P4', 'P5', 'Closed Incidents', 'SLA Miss'],
  change: ['Change Requests', 'In Progress', 'Closed', 'Not Closed', 'SLA Met', 'SLA Missed'],
  service: ['Service Requests', 'Open', 'In Progress', 'Pending', 'Closed Incomplete', 'Closed Complete'],
  problemTickets: ['Problem Tickets', 'Open', 'Known Errors', 'Reactive', 'Made SLA', 'RCA'],
};

const resolveItsmUrl = (type: string): string | undefined => {
  const group = Object.keys(itsmGroups).find(groupKey =>
    itsmGroups[groupKey].includes(type)
  );

  return group ? itsmEndpoints[group] : undefined;
};

const ensureIncidentStateArray = (params: Record<string, any>): Record<string, any> => {
  let requestParams = params ?? {};
  if (
    requestParams?.filter?.incident_state !== undefined &&
    requestParams?.filter?.incident_state !== null &&
    !Array.isArray(requestParams.filter.incident_state)
  ) {
    requestParams = {
      ...requestParams,
      filter: {
        ...requestParams.filter,
        incident_state: [requestParams.filter.incident_state],
      },
    };
  }
  if (
    requestParams?.incident_state !== undefined &&
    requestParams?.incident_state !== null &&
    !Array.isArray(requestParams.incident_state)
  ) {
    requestParams = {
      ...requestParams,
      incident_state: [requestParams.incident_state],
    };
  }
  return requestParams;
};

const normalizeDandAIAGFilter = (params: Record<string, any>): Record<string, any> => {
  let requestParams = params ?? {};
  if (requestParams?.filter?.isDandAIAG !== undefined) {
    const rawVal = requestParams.filter.isDandAIAG;
    if (typeof rawVal === 'string') {
      requestParams = {
        ...requestParams,
        filter: {
          ...requestParams.filter,
          isDandAIAG: rawVal.toLowerCase() === 'yes' || rawVal.toLowerCase() === 'true',
        },
      };
    } else if (Array.isArray(rawVal) && rawVal.length > 0) {
      const first = rawVal[0]?.value ?? rawVal[0];
      requestParams = {
        ...requestParams,
        filter: {
          ...requestParams.filter,
          isDandAIAG: String(first).toLowerCase() === 'yes' || String(first).toLowerCase() === 'true',
        },
      };
    }
  }
  return requestParams;
};

export const getITSMSummary = async (params: Record<string, any>, type: string): Promise<any> => {
  const endpoint = itsm_summary_url[type];
  let requestParams = params ?? {};
  if (endpoint === itsm_summary_url['Incidents'] || type === 'Incidents') {
    requestParams = ensureIncidentStateArray(requestParams);
  }
  requestParams = normalizeDandAIAGFilter(requestParams);

  const response = await axios.post(endpoint, requestParams, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getITSMData = async (params: Record<string, any>, type: string): Promise<any> => {
  const endpoint = resolveItsmUrl(type);

  if (!endpoint) {
    throw new Error(`No endpoint mapped for type: ${type}`);
  }

  let requestParams = params ?? {};
  if (endpoint === itsmEndpoints.incidents) {
    requestParams = ensureIncidentStateArray(requestParams);
  }
  requestParams = normalizeDandAIAGFilter(requestParams);

  const response = await axios.post(endpoint, requestParams, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getGroupedChildren = async (params: any): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/fetchGroupChildren`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getOpsTrendBySector = async (params: any): Promise<TrendResponse> => {
  const response = await axios.post(`${url}/unifiedOps/trend/sector`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getOpsTrendByProgram = async (params: any): Promise<TrendResponse> => {
  const response = await axios.post(`${url}/unifiedOps/trend/program`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getOpsMostImpactedApps = async (params: any): Promise<any[]> => {
  const response = await axios.post(`${url}/unifiedOps/trend/most-impacted-apps`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const exportITSMAlerts = async (params: Record<string, any>, type: string): Promise<void> => {
  const endpoint = resolveItsmUrl(type);
  if (!endpoint) {
    throw new Error(`No endpoint mapped for type: ${type}`);
  }
  const response = await axios.post(endpoint, { ...params, exportType: 'excel' }, {
    headers: getAuthorazieHeader(),
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  const disposition = response.headers['content-disposition'] ?? '';
  const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? `UnifiedAlerts_${type}_${new Date().toISOString()}.xlsx`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getOpsSummary = async (params: Record<string, any>): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/ops-summary`, params ?? {}, {
    headers: getAuthorazieHeader()
  });
  return response.data.data?.data;
};

export const getIncidentSummary = async (params: Record<string, any>): Promise<any> => {
  let requestParams = ensureIncidentStateArray(params);
  requestParams = normalizeDandAIAGFilter(requestParams);
  const response = await axios.post(`${url}/unifiedOps/incident-summary`, requestParams, {
    headers: getAuthorazieHeader()
  });
  return response.data.data?.data;
};

export const getProblemSummary = async (params: Record<string, any>): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/problem-summary`, params ?? {}, {
    headers: getAuthorazieHeader()
  });
  return response.data.data?.data;
};

export const getServiceSummary = async (params: Record<string, any>): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/service-summary`, params ?? {}, {
    headers: getAuthorazieHeader()
  });
  return response.data.data?.data;
};

export const getChangeSummary = async (params: Record<string, any>): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/change-summary`, params ?? {}, {
    headers: getAuthorazieHeader()
  });
  return response.data.data?.data;
};


export const getIncidentsFiltersData = async (): Promise<any> => {
  const response = await axios.get(`${url}/unifiedOps/load-filters`, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const saveIncidentsFiltersData = async (
  params: {
    filters?: any;
    activeParentTile: string;
    activeSubTile: string;
    activeTile: string;
  },
  liveTile: string
): Promise<any> => {
  const response = await axios.post(
    `${url}/unifiedOps/store-filters/${liveTile}`,
    params,
    {
      headers: getAuthorazieHeader()
    }
  );

  return response.data.data;
};


export const createIncidentComments = async (params: { incidentId: string; comments: string }): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/incident-comments`, params, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getIncidentDropDownData = async (params: Record<string, any>): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/dropdown-data`, params ?? {}, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const createSop = async (
  sopName: string,
  sopLink: string,
  type: string,
  assignedApprover?: string | null,
  approvalRequired?: boolean
) => {
  const headers = getAuthorazieHeader();

  const requestBody: any = {
    sopName,
    sopLink,
    type
  };

  if (assignedApprover !== undefined) {
    requestBody.assignedApprover = assignedApprover;
  }
  if (approvalRequired !== undefined) {
    requestBody.approvalRequired = approvalRequired;
  }

  try {
    const res = await axios.post(`${BASE_URL}/create-sop`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const updateSop = async (
  sopId: string,
  sopName: string,
  sopLink: string,
  type: string,
  assignedApprover?: string | null,
  approvalRequired?: boolean
) => {
  const headers = getAuthorazieHeader();

  const requestBody: any = {
    sopId,
    sopName,
    sopLink,
    type
  };

  if (assignedApprover !== undefined) {
    requestBody.assignedApprover = assignedApprover;
  }
  if (approvalRequired !== undefined) {
    requestBody.approvalRequired = approvalRequired;
  }

  try {
    const res = await axios.post(`${BASE_URL}/update-sop`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const attachAlertsSop = async (
  sopId: string,
  alertIds: string[],
  type: string
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    sopId,
    alertIds,
    type
  };

  try {
    const res = await axios.post(`${BASE_URL}/attach-alerts-sop`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSopMetrics = async (
  type: string = 'Incidents',
  startDate?: string,
  endDate?: string
) => {
  const headers = getAuthorazieHeader();

  const requestBody: any = {
    type
  };
  if (startDate) requestBody.startDate = startDate;
  if (endDate) requestBody.endDate = endDate;

  try {
    const res = await axios.post(`${BASE_URL}/sop-metrics`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSopList = async (
  type: string,
  page: number,
  limit: number,
  sortingSopInfo: { sortBy: string; sortDir: string },
  searchValue: string,
  statusFilter?: string
) => {
  const headers = getAuthorazieHeader();

  let sortField = sortingSopInfo.sortBy;
  if (sortField === 'name') sortField = 'sopName';
  if (sortField === 'owner') sortField = 'assignedApprover';
  if (sortField === 'createdBy') sortField = 'createdByEmail';
  if (sortField === 'updatedBy') sortField = 'updatedByEmail';
  if (sortField === 'lastUpdated') sortField = 'updatedAt';

  const requestBody: any = {
    type,
    page,
    sortField,
    sortOrder: sortingSopInfo.sortDir,
    limit,
    searchValue
  };

  if (statusFilter) {
    requestBody.statusFilter = statusFilter;
  }

  try {
    const res = await axios.post(`${BASE_URL}/sop-list`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteSop = async (
  sopId: string
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    sopId
  };

  try {
    const res = await axios.post(`${BASE_URL}/delete-sop`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const approveSop = async (sopId: string, comments: string = '') => {
  const headers = getAuthorazieHeader();
  const requestBody = { sopId, comments };
  try {
    const res = await axios.post(`${BASE_URL}/approve-sop`, requestBody, { headers });
    return res.data;
  } catch (err: any) {
    return err;
  }
};

export const rejectSop = async (sopId: string, comments: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = { sopId, comments };
  try {
    const res = await axios.post(`${BASE_URL}/reject-sop`, requestBody, { headers });
    return res.data;
  } catch (err: any) {
    return err;
  }
};

export const generateRCA = async (row: any): Promise<any> => {
  const payload = {
    sys_id: row.inc_sys_id || row.sys_id || row.alertId || '',
    number: row.incident || row.number || '',
    short_description: row.short_description || row.description || '',
    description: row.description || '',
    cmdb_ci: row.cmdb_ci || '',
    priority: row.priority || row.severity || '',
    category: row.category || '',
    assignment_group: row.assignment_group || '',
    opened_at: row.opened_at || row.firedDateTime || new Date().toISOString(),
  };

  const response = await axios.post(`${aiPilotUrl}/rca/generate`, payload, {
    headers: getAuthorazieHeader()
  });
  return response.data;
};

export const getRcaStatus = async (incidentId: string): Promise<any> => {
  const response = await axios.get(`${aiPilotUrl}/rca/incidents/${incidentId}/status`, {
    headers: getAuthorazieHeader()
  });
  return response.data;
};

export const getRcaReport = async (incidentId: string): Promise<any> => {
  const response = await axios.get(`${aiPilotUrl}/rca/incidents/${incidentId}`, {
    headers: getAuthorazieHeader()
  });
  return response.data;
};


export const getTaskRecordList = async (payload: {
    page : number,
    pageSize : number,
    isExcelExport : boolean,
    parentId : string | null,
  parentTile: string,
  sortBy: string,
  sortDir: string
}): Promise<any> => {
  const response = await axios.post(`${url}/unifiedOps/task-records`, payload, {
    headers: getAuthorazieHeader()
  });
  return response.data.data;
};

export const getDuplicateAlerts = async (id: string): Promise<any> => {
  const response = await axios.get(`${BASE_URL}/duplicate-alerts/${id}`, {
    headers: getAuthorazieHeader()
  });

  return response.data.data;
};